import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useAdminUserSession } from '../../models/hooks/adminStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_API_KEY = 'surveyTemplatesByIndustry';

export interface ISurveyTemplatesByIndustryQueryParams {
	industry: string;
	status?: Api.TemplateStatus;
	enabled?: boolean;
}

/**
 * Admin: Get all survey templates for a specific industry with optional status filtering.
 * Returns a collection of template cards for the specified industry.
 *
 * @param params - Query parameters including industry name and optional status filter
 * @returns React Query result with survey template cards
 */
export function useSurveyTemplatesByIndustryQuery(params: ISurveyTemplatesByIndustryQueryParams) {
	const { industry, status, enabled = true } = params;
	const userSession = useAdminUserSession();

	return useQuery({
		queryFn: () => {
			const urlPath =
				status !== undefined
					? `admin/surveytemplate/Industry/${industry}?status=${status}`
					: `admin/surveytemplate/Industry/${industry}`;

			return userSession.webServiceHelper.callAsync<Api.ISystemSurveyTemplate[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath,
				}),
				'GET'
			);
		},
		queryKey: [BASE_API_KEY, industry, status],
		enabled: enabled && !!industry,
		staleTime: 10 * 60 * 1000, // 10 minutes
	});
}

export const invalidateSurveyTemplatesByIndustryQuery = (industry: string) => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY, industry] });
};
